import type { MemberColor } from '@awwyeah/shared';

/** A household member (or invite-time colour hint) using a given colour. */
export interface ColorHolder {
  name: string;
  avatarEmoji?: string;
}

/**
 * Groups a flat member/hint list by colour so the picker can badge taken swatches and show
 * "Faye's colour" hints. Pure so it's testable without rendering anything (2026-08-01,
 * usability testing: Dan and Faye ended up with near-identical colours — the picker should
 * make "this one's taken" visible at a glance, without making taken colours unpickable).
 */
export function groupByColor(
  holders: readonly { color: string; name: string; avatarEmoji?: string }[],
): Map<MemberColor, ColorHolder[]> {
  const map = new Map<MemberColor, ColorHolder[]>();
  for (const holder of holders) {
    const color = holder.color as MemberColor;
    const existing = map.get(color);
    const entry: ColorHolder = { name: holder.name, avatarEmoji: holder.avatarEmoji };
    if (existing) {
      existing.push(entry);
    } else {
      map.set(color, [entry]);
    }
  }
  return map;
}

/** "Faye's colour" / "Faye & Dan's colour" — the hint line under a taken swatch. */
export function takenHint(holders: readonly ColorHolder[]): string {
  if (holders.length === 0) return '';
  const names = holders.map((h) => h.name);
  const joined =
    names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
  return `${joined}'s colour`;
}
